import { useEffect, useRef } from 'react';

// Load Tally's official embed widget exactly once.
let tallyScriptPromise = null;
const loadTallyEmbedScript = () => {
  if (typeof window === 'undefined') return Promise.resolve();
  if (window.Tally && typeof window.Tally.loadEmbeds === 'function') {
    return Promise.resolve();
  }
  if (tallyScriptPromise) return tallyScriptPromise;
  tallyScriptPromise = new Promise((resolve) => {
    const existing = document.querySelector('script[src="https://tally.so/widgets/embed.js"]');
    if (existing) {
      existing.addEventListener('load', () => resolve());
      if (window.Tally) resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://tally.so/widgets/embed.js';
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => resolve();
    document.body.appendChild(script);
  });
  return tallyScriptPromise;
};

/**
 * Embed a Tally form using the official `data-tally-src` pattern. Tally's
 * widget script processes the iframe, sets its `src`, and manages the iframe
 * height directly via DOM mutation so the form renders without an internal
 * scrollbar across all breakpoints. We additionally listen to Tally.FormResize
 * postMessage events as a defence-in-depth fallback (the iframe height
 * attribute will be updated even if the script fails to load).
 */
export const TallyEmbed = ({ formId, title, params = {} }) => {
  const iframeRef = useRef(null);

  const search = new URLSearchParams({
    alignLeft: '1',
    hideTitle: '1',
    transparentBackground: '1',
    dynamicHeight: '1',
    ...params,
  });
  const src = `https://tally.so/embed/${formId}?${search.toString()}`;

  useEffect(() => {
    // postMessage listener as a fallback. Tally's official script also wires
    // this up; whichever runs first sets the height.
    const onMessage = (event) => {
      if (typeof event.data !== 'string') return;
      try {
        const data = JSON.parse(event.data);
        if (
          data?.event === 'Tally.FormResize' &&
          data?.payload?.formId === formId &&
          typeof data.payload.height === 'number' &&
          iframeRef.current
        ) {
          // Mutate DOM directly so React does not race with us / Tally's script.
          iframeRef.current.style.height = `${data.payload.height}px`;
          iframeRef.current.setAttribute('height', String(data.payload.height));
        }
      } catch (_) {
        // Ignore non-JSON postMessages.
      }
    };
    window.addEventListener('message', onMessage);

    loadTallyEmbedScript().then(() => {
      if (window.Tally && typeof window.Tally.loadEmbeds === 'function') {
        window.Tally.loadEmbeds();
      }
    });

    return () => window.removeEventListener('message', onMessage);
  }, [formId]);

  // NOTE: only `data-tally-src` is set initially. Tally's widget script copies
  // it onto `src`. We intentionally omit `src` so the script will process the
  // iframe (it skips iframes that already have a src).
  return (
    <iframe
      ref={iframeRef}
      data-tally-src={src}
      width="100%"
      height="500"
      scrolling="no"
      style={{ height: '500px', border: 'none', overflow: 'hidden', minWidth: '100%' }}
      frameBorder="0"
      marginHeight="0"
      marginWidth="0"
      title={title}
      className="w-full bg-transparent block"
      allow="fullscreen"
      loading="eager"
    />
  );
};

export default TallyEmbed;
